import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { useAppStore } from '@/lib/store';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { fetchExchangeRate } from '@/lib/currency-rates';
import { useUserStore } from '@/lib/user-store';
import Navbar from '@/components/Navbar';
import BottomNav from '@/components/BottomNav';
import HomePage from '@/pages/HomePage';
import ShopPage from '@/pages/ShopPage';
import ShopComingSoonPage from '@/pages/ShopComingSoonPage';
import ArtworkDetailPage from '@/pages/ArtworkDetailPage';
import ArtistsPage from '@/pages/ArtistsPage';
import ArtistDetailPage from '@/pages/ArtistDetailPage';
import ExhibitionsPage from '@/pages/ExhibitionsPage';
import ExhibitionDetailPage from '@/pages/ExhibitionDetailPage';
import ArtFairsPage from '@/pages/ArtFairsPage';
import ArtFairDetailPage from '@/pages/ArtFairDetailPage';
import TeamPage from '@/pages/TeamPage';
import CheckoutPage from '@/pages/CheckoutPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ProfilePage from '@/pages/ProfilePage';
import ContactPage from '@/pages/ContactPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DropshopPage from '@/pages/DropshopPage';
import DropshopGate from '@/components/DropshopGate';
import DropshopDetailPage from '@/pages/DropshopDetailPage';
import NewsPage from '@/pages/NewsPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import AboutPage from '@/pages/AboutPage';
import PartnersPage from '@/pages/PartnersPage';
import TermsPage from '@/pages/TermsPage';
import ShippingReturnsPage from '@/pages/ShippingReturnsPage';
import CareersPage from '@/pages/CareersPage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentCancelPage from '@/pages/PaymentCancelPage';
import CartDrawer from '@/components/CartDrawer';
import SocialSidebar from '@/components/SocialSidebar';
import Newsletter from '@/components/Newsletter';
import AdminGuard from '@/components/admin/AdminGuard';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminArtworksPage from '@/pages/admin/AdminArtworksPage';
import AdminArtworkEditorPage from '@/pages/admin/AdminArtworkEditorPage';
import AdminCategoriesPage from '@/pages/admin/AdminCategoriesPage';
import AdminArtistsPage from '@/pages/admin/AdminArtistsPage';
import AdminArtistEditorPage from '@/pages/admin/AdminArtistEditorPage';
import AdminExhibitionsPage from '@/pages/admin/AdminExhibitionsPage';
import AdminExhibitionEditorPage from '@/pages/admin/AdminExhibitionEditorPage';
import AdminArtFairsPage from '@/pages/admin/AdminArtFairsPage';
import AdminArtFairEditorPage from '@/pages/admin/AdminArtFairEditorPage';
import AdminDropshopPage from '@/pages/admin/AdminDropshopPage';
import AdminDropshopEditorPage from '@/pages/admin/AdminDropshopEditorPage';
import AdminTeamPage from '@/pages/admin/AdminTeamPage';
import AdminTeamEditorPage from '@/pages/admin/AdminTeamEditorPage';
import AdminOrdersPage from '@/pages/admin/AdminOrdersPage';
import AdminOrderDetailPage from '@/pages/admin/AdminOrderDetailPage';
import AdminReviewsPage from '@/pages/admin/AdminReviewsPage';
import AdminInboxPage from '@/pages/admin/AdminInboxPage';
import AdminSubscribersPage from '@/pages/admin/AdminSubscribersPage';
import AdminNewsPage from '@/pages/admin/AdminNewsPage';
import AdminNewsEditorPage from '@/pages/admin/AdminNewsEditorPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import AdminFAQPage from '@/pages/admin/AdminFAQPage';
import AdminPartnersPage from '@/pages/admin/AdminPartnersPage';
import AdminPageEditor from '@/pages/admin/AdminPageEditorPage';
import FAQPage from '@/pages/FAQPage';

const App: React.FC = () => {
  const isDarkMode = useAppStore((s) => s.isDarkMode);
  const language = useAppStore((s) => s.language);
  const initialized = useAdminStore((s) => s.initialized);
  const settings = useAdminStore((s) => s.settings);
  const currentUser = useUserStore((s) => s.currentUser);
  // The whitelist lives on the server; the API answers shopUnlocked for whoever is asking.
  // Keeping the addresses out of the bundle stops the tester list from being public, and
  // the same answer drives the price data the API returns — the two cannot drift apart.
  const showComingSoon = settings?.shopComingSoon !== false && !settings?.shopUnlocked;
  const menuVis = settings?.menuVisibility;
  const { t, localize } = useTranslation();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  // Scroll to top on every navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.lang = language;
    if (language === 'ka') {
      document.documentElement.classList.add('lang-ka');
    } else {
      document.documentElement.classList.remove('lang-ka');
    }
  }, [language]);

  // Set browser tab title from gallery name
  useEffect(() => {
    const name = localize(settings?.galleryName);
    if (name) document.title = name;
  }, [settings?.galleryName, language, localize]);

  // Loading timeout — don't block forever if API is slow
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const showLoader = !initialized && !isAdmin && !loadingTimeout;

  // Initialize data from API on app load
  useEffect(() => {
    useAdminStore.getState().initialize();
    // Restore user session from JWT token
    useUserStore.getState().restoreSession();
    // Safety: never block longer than 2 seconds
    const timer = setTimeout(() => setLoadingTimeout(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Apply admin default currency if user hasn't chosen one
  useEffect(() => {
    if (!settings?.defaultCurrency) return;
    const userChose = localStorage.getItem('gallery-currency');
    if (!userChose) {
      useAppStore.getState().setCurrency(settings.defaultCurrency);
    }
  }, [settings?.defaultCurrency]);

  // Fetch NBG exchange rate on app load
  useEffect(() => {
    const setExchangeRate = useAppStore.getState().setExchangeRate;
    fetchExchangeRate().then((rate) => setExchangeRate(rate));
  }, []);

  // Show branded loading screen while data is being fetched from API (max 4s)
  if (showLoader) {
    return (
      <div className="min-h-screen bg-white dark:bg-black flex flex-col items-center justify-center transition-colors duration-700">
        <h1 className="text-3xl sm:text-4xl font-syncopate font-bold tracking-tighter text-black dark:text-white logo-brand mb-6">
          FOTOATELIER.
        </h1>
        <div className="w-8 h-8 border-2 border-black/10 dark:border-white/10 border-t-black dark:border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (isAdmin) {
    return (
      <AdminGuard>
        <Routes>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/artworks" element={<AdminArtworksPage />} />
            <Route path="/admin/artworks/new" element={<AdminArtworkEditorPage />} />
            <Route path="/admin/artworks/:id" element={<AdminArtworkEditorPage />} />
            <Route path="/admin/categories" element={<AdminCategoriesPage />} />
            <Route path="/admin/artists" element={<AdminArtistsPage />} />
            <Route path="/admin/artists/new" element={<AdminArtistEditorPage />} />
            <Route path="/admin/artists/:id" element={<AdminArtistEditorPage />} />
            <Route path="/admin/exhibitions" element={<AdminExhibitionsPage />} />
            <Route path="/admin/exhibitions/new" element={<AdminExhibitionEditorPage />} />
            <Route path="/admin/exhibitions/:id" element={<AdminExhibitionEditorPage />} />
            <Route path="/admin/art-fairs" element={<AdminArtFairsPage />} />
            <Route path="/admin/art-fairs/new" element={<AdminArtFairEditorPage />} />
            <Route path="/admin/art-fairs/:id" element={<AdminArtFairEditorPage />} />
            <Route path="/admin/dropshop" element={<AdminDropshopPage />} />
            <Route path="/admin/dropshop/new" element={<AdminDropshopEditorPage />} />
            <Route path="/admin/dropshop/:id" element={<AdminDropshopEditorPage />} />
            <Route path="/admin/team" element={<AdminTeamPage />} />
            <Route path="/admin/team/new" element={<AdminTeamEditorPage />} />
            <Route path="/admin/team/:id" element={<AdminTeamEditorPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/orders/:id" element={<AdminOrderDetailPage />} />
            <Route path="/admin/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/inbox" element={<AdminInboxPage />} />
            <Route path="/admin/subscribers" element={<AdminSubscribersPage />} />
            <Route path="/admin/news" element={<AdminNewsPage />} />
            <Route path="/admin/news/new" element={<AdminNewsEditorPage />} />
            <Route path="/admin/news/:id" element={<AdminNewsEditorPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/faq" element={<AdminFAQPage />} />
            <Route path="/admin/partners" element={<AdminPartnersPage />} />
            <Route path="/admin/pages/about" element={<AdminPageEditor pageType="about" title="About Page" />} />
            <Route path="/admin/pages/terms" element={<AdminPageEditor pageType="terms" title="Terms & Conditions" />} />
            <Route path="/admin/pages/privacy" element={<AdminPageEditor pageType="privacy" title="Privacy Policy" />} />
            <Route path="/admin/pages/cookie" element={<AdminPageEditor pageType="cookie" title="Cookie Policy" />} />
            <Route path="/admin/pages/careers" element={<AdminPageEditor pageType="careers" title="Careers" />} />
            <Route path="/admin/pages/shipping" element={<AdminPageEditor pageType="shipping" title="Shipping & Returns" />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
          </Route>
        </Routes>
      </AdminGuard>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-700">
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-black dark:bg-white origin-left z-[600]"
        style={{ scaleX }}
      />

      <Navbar />
      <BottomNav />
      <CartDrawer />
      <SocialSidebar />

      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={showComingSoon ? <ShopComingSoonPage /> : <ShopPage />} />
          <Route path="/artwork/:slug" element={showComingSoon ? <ShopComingSoonPage /> : <ArtworkDetailPage />} />
          <Route path="/artists" element={<ArtistsPage />} />
          <Route path="/artists/:slug" element={<ArtistDetailPage />} />
          <Route path="/exhibitions" element={<ExhibitionsPage />} />
          <Route path="/exhibitions/:slug" element={<ExhibitionDetailPage />} />
          <Route path="/art-fairs" element={<ArtFairsPage />} />
          <Route path="/art-fairs/:slug" element={<ArtFairDetailPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dropshop" element={<DropshopGate><DropshopPage /></DropshopGate>} />
          <Route path="/dropshop/:slug" element={<DropshopGate><DropshopDetailPage /></DropshopGate>} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/news" element={menuVis?.news === false ? <Navigate to="/" replace /> : <NewsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/faq" element={menuVis?.faq === false ? <Navigate to="/" replace /> : <FAQPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/partners" element={menuVis?.partners === false ? <Navigate to="/" replace /> : <PartnersPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/checkout/success" element={<PaymentSuccessPage />} />
          <Route path="/checkout/cancel" element={<PaymentCancelPage />} />
        </Routes>
      </main>

      {settings?.sectionVisibility?.newsletter !== false && <Newsletter />}

      {settings?.sectionVisibility?.siteFooter !== false && (
      <footer className="py-16 pb-28 lg:pb-16 px-6 sm:px-10 bg-[#f9f9f9] dark:bg-[#0a0a0a] border-t border-black/5 dark:border-white/5 transition-colors duration-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-10 md:gap-16">
            {/* Brand */}
            <div className="shrink-0">
              <h2 className="text-3xl sm:text-4xl font-syncopate font-bold tracking-tighter text-black dark:text-white logo-brand">
                FOTOATELIER.
              </h2>
              {localize(settings?.footerText) && (
                <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20 mt-2">
                  {localize(settings?.footerText)}
                </p>
              )}
            </div>

            {/* Links */}
            <div className="flex gap-10 sm:gap-14 flex-wrap">
              <div className="space-y-3">
                <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-black/30 dark:text-white/30">
                  {localize(settings?.footerAboutTitle) || (t.nav.home === 'Home' ? 'About Us' : 'ჩვენს შესახებ')}
                </p>
                <div className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-widest text-black/50 dark:text-white/50">
                  <Link to="/about" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.nav.home === 'Home' ? 'About Us' : 'ჩვენს შესახებ'}
                  </Link>
                  <Link to="/team" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.nav.team}
                  </Link>
                  {menuVis?.partners !== false && (
                    <Link to="/partners" className="hover:text-black dark:hover:text-white transition-colors">
                      {t.nav.home === 'Home' ? 'Partners' : 'პარტნიორები'}
                    </Link>
                  )}
                  <Link to="/contact" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.nav.contact}
                  </Link>
                  <Link to="/news" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.news.title}
                  </Link>
                  <Link to="/careers" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.nav.home === 'Home' ? 'Careers' : 'კარიერა'}
                  </Link>
                </div>
              </div>
              <div className="space-y-3">
                <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-black/30 dark:text-white/30">
                  {localize(settings?.footerLegalTitle) || (t.nav.home === 'Home' ? 'Legal' : 'სამართლებრივი')}
                </p>
                <div className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-widest text-black/50 dark:text-white/50">
                  <Link to="/privacy-policy" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.legal.privacyPolicy}
                  </Link>
                  <Link to="/cookie-policy" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.legal.cookiePolicy}
                  </Link>
                  {menuVis?.terms !== false && (
                    <Link to="/terms" className="hover:text-black dark:hover:text-white transition-colors">
                      {t.legal.termsConditions}
                    </Link>
                  )}
                  <Link to="/faq" className="hover:text-black dark:hover:text-white transition-colors">
                    FAQ
                  </Link>
                  <Link to="/shipping-returns" className="hover:text-black dark:hover:text-white transition-colors">
                    {t.nav.home === 'Home' ? 'Shipping & Returns' : 'მიწოდება და დაბრუნება'}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom line */}
          <div className="mt-12 pt-6 border-t border-black/5 dark:border-white/5">
            <p className="text-[8px] font-bold uppercase tracking-[0.3em] text-black/15 dark:text-white/15 text-center">
              &copy; {new Date().getFullYear()} {localize(settings?.galleryName) || 'Fotoatelier'} &middot; All rights reserved
            </p>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
};

export default App;
